import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getServiceRequests, getWorkOrders, getInvoices, getReports, getQuotes, openCustomerReport } from '../../services/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardList, Receipt, FileText, Plus, ArrowRight, FileSignature } from 'lucide-react';
import { StatCard, PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { SitePerformance } from '../../components/SitePerformance';

const CustomerOverview = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ requests: [], orders: [], invoices: [], reports: [], quotes: [] });
  useEffect(() => {
    Promise.all([
      getServiceRequests(), getWorkOrders(), getInvoices(), getReports(),
      getQuotes().catch(() => []),
    ]).then(([r, o, i, p, q]) => setData({ requests: r, orders: o, invoices: i, reports: p, quotes: q }));
  }, []);
  const outstanding = data.invoices.filter(i => i.status === 'pending').reduce((s, i) => s + i.amount, 0);
  const pendingQuotes = data.quotes.filter(q => q.status === 'sent');
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title={`Welcome, ${user.name.split(' ')[0]}`} description={user.company}
        actions={<Link to="/customer/requests"><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> New Request</Button></Link>} />

      {pendingQuotes.length > 0 && (
        <Link
          to="/customer/quotes"
          data-testid="pending-quotes-banner"
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 hover:border-amber-300 hover:bg-amber-100 rounded-md p-4 transition-colors"
        >
          <FileSignature className="h-6 w-6 text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display uppercase tracking-wide text-sm text-amber-900">
              {pendingQuotes.length} quote{pendingQuotes.length === 1 ? '' : 's'} awaiting your decision
            </div>
            <div className="text-xs text-amber-800 truncate">Click to review and accept or decline.</div>
          </div>
          <ArrowRight className="h-4 w-4 text-amber-700" />
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={ClipboardList} label="Open Requests" value={data.requests.filter(r => r.status !== 'completed').length} hint={`${data.requests.length} total`} accent="blue" to="/customer/requests" />
        <StatCard icon={ClipboardList} label="Active Jobs" value={data.orders.filter(w => w.status !== 'completed').length} hint="On site" accent="amber" to="/customer/work-orders?status=active" />
        <StatCard icon={ClipboardList} label="Completed Jobs" value={data.orders.filter(w => w.status === 'completed').length} hint="Historical work" accent="green" to="/customer/work-orders?status=completed" />
        <StatCard icon={Receipt} label="Outstanding" value={`£${outstanding.toFixed(2)}`} hint={`${data.invoices.filter(i => i.status === 'pending').length} pending`} accent={outstanding > 0 ? 'red' : 'green'} to="/customer/invoices" />
        <StatCard icon={FileText} label="Reports Available" value={data.reports.length} hint="All time" accent="slate" to="/customer/reports" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-200"><h2 className="font-display uppercase tracking-wide">Recent Requests</h2><Link to="/customer/requests" className="text-xs font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link></div>
          <div className="divide-y divide-slate-100">
            {data.requests.slice(0, 4).map(r => (
              <div key={r.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center gap-2 mb-1"><span className="text-xs font-mono text-slate-500">{r.id}</span><PriorityBadge priority={r.priority} /><StatusBadge status={r.status} /></div>
                <div className="text-sm font-medium text-slate-900">{r.title}</div>
              </div>
            ))}
            {data.requests.length === 0 && <div className="p-8 text-center text-slate-500">No requests yet.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-200"><h2 className="font-display uppercase tracking-wide">Recent Reports</h2><Link to="/customer/reports" className="text-xs font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link></div>
          <div className="divide-y divide-slate-100">
            {data.reports.slice(0, 4).map(r => (
              <button
                key={r.id}
                type="button"
                onClick={async () => {
                  try { await openCustomerReport(r); }
                  catch (e) { toast.error(e.message || 'Could not open report'); }
                }}
                className="w-full text-left p-4 hover:bg-slate-50 flex items-center justify-between"
              >
                <div>
                  <div className="text-xs font-mono text-slate-500">{r.id}</div>
                  <div className="text-sm font-medium text-slate-900">{r.type}</div>
                  <div className="text-xs text-slate-500">Issued {r.issuedAt} · {r.engineer}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
            ))}
            {data.reports.length === 0 && <div className="p-8 text-center text-slate-500">No reports yet.</div>}
          </div>
        </div>
      </div>

      <SitePerformance variant="customer" />
    </div>
  );
};

export default CustomerOverview;
